import * as ActionTypes from './actionTypes'
import {LOAD_PROJECT_ASSIGNMENTS_EDITOR_DATA_SUCCESS, LOAD_TASK_ASSIGNMENTS_EDITOR_DATA_SUCCESS} from './actionTypes'
import {createActionObject} from '../../../redux/utils'
import {rpcCall} from '../../../util/rpc/rpcCalls'
import {
    getCustomerIdsOrder,
    getCustomersSearchText,
    getEditingLevel,
    getProjectIdsOrder,
    getProjectsSearchText,
    getTaskIdsOrder,
    getTasksSearchText,
    needMoreCustomers,
    needMoreProjects,
    needMoreTasks,
    showOnlyActiveEntities,
} from './selectors'
import * as CreatePanelActions from '../../../panels/user/createPanel/actions'
import * as CreatePanelSelectors from '../../../panels/user/createPanel/selectors'
import * as EntitiesActions from '../../../entities/actions'
import actionsGenerator from '../hierarchyAssignmentsEditor/actionsGenerator'
import {setManagementAssignmentsCountsLoaded} from '../../../panels/user/userData/actions'
import * as AssignmentsPanelActions from '../createManagementAssignmentsPanel/actions'
import {getCustomers, getProjects, getTasks} from '../../../entities/selectors'
import {arrayToSet} from '../../../atUtils'

export const hierarchyAssignmentsEditorActions = actionsGenerator({
    actionTypes: ActionTypes
})

const LOAD_LIMIT = 20

export const openEditor = () => dispatch => {
    dispatch(hierarchyAssignmentsEditorActions.resetSearchState())
    dispatch(hierarchyAssignmentsEditorActions.clearAll())
    dispatch(createActionObject(ActionTypes.SHOW_SET, true))
}

export const closeEditor = () => dispatch => {
    dispatch(createActionObject(ActionTypes.SHOW_SET, false))
}

export const selectCustomer = hierarchyAssignmentsEditorActions.selectCustomer

export const selectProject = hierarchyAssignmentsEditorActions.selectProject

export const deselectCustomer = hierarchyAssignmentsEditorActions.deselectCustomer

export const deselectProject = hierarchyAssignmentsEditorActions.deselectProject

export const loadMoreEntities = () => dispatch => {
    dispatch(loadMoreCustomers())
    dispatch(loadMoreProjects())
    dispatch(loadMoreTasks())
}

export const loadMoreCustomers = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreCustomers(state)) {
        const assignedIds = CreatePanelSelectors.getManagementAssignmentsCptIds(state)
        const onlyActive = showOnlyActiveEntities(state)
        const searchText = getCustomersSearchText(state)
        const offset = getCustomerIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_CUSTOMER_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getCustomerManagementAssignmentsEditorDataForCreateUser', [assignedIds, onlyActive, searchText, offset, LOAD_LIMIT])
            .then(data => {
                const someCustomersOrLowerAssigned = assignedIds.customerIds.length > 0
                                                     || assignedIds.customerIdsExcludingSelf.length > 0
                                                     || assignedIds.projectIds.length > 0
                                                     || assignedIds.projectIdsExcludingSelf.length > 0
                                                     || assignedIds.taskIds.length > 0
                data = {
                    ...data,
                    someCustomersOrLowerAssigned: someCustomersOrLowerAssigned,
                    allCustomersAssigned: data.customersCount === assignedIds.customerIds.length && assignedIds.customerIds.length !== 0,
                }
                dispatch(EntitiesActions.addCustomers(data['customers'].map(c => c['customer'])))
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersSuccess(data, LOAD_LIMIT))
                dispatch(CreatePanelActions.setCustomersCount(data.customersCount))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(hierarchyAssignmentsEditorActions.loadCustomersFailure(error))
            })
    }
}

export const loadMoreProjects = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreProjects(state)) {
        const assignedIds = CreatePanelSelectors.getManagementAssignmentsCptIds(state)
        const customerId = getEditingLevel(state).customerId
        const onlyActive = showOnlyActiveEntities(state)
        const searchText = getProjectsSearchText(state)
        const offset = getProjectIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_PROJECT_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getProjectManagementAssignmentsEditorDataForCreateUser', [assignedIds, customerId, onlyActive, searchText, offset, LOAD_LIMIT])
            .then(data => {
                const projects = getProjects(getState())
                const tasks = getTasks(getState())

                const assignedProjectIdsInCurrentCustomer = [
                    ...assignedIds.projectIds,
                    ...assignedIds.projectIdsExcludingSelf
                ].filter(id => projects[id].customerId === customerId)

                const assignedTaskIdsInCurrentCustomer = assignedIds.taskIds.filter(id => tasks[id].customerId === customerId)

                const someProjectsOrLowerAssigned = assignedProjectIdsInCurrentCustomer.length > 0
                                              || assignedTaskIdsInCurrentCustomer.length > 0
                data = {
                    ...data,
                    futureProjectsAssigned: assignedIds.customerIdsExcludingSelf.includes(customerId),
                    someProjectsOrLowerAssigned,
                    allProjectsAssigned: data.projectsCount === assignedProjectIdsInCurrentCustomer.length && assignedProjectIdsInCurrentCustomer.length !== 0,
                }
                dispatch(EntitiesActions.addProjects(data['projects'].map(p => p['project'])))
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsSuccess(data, LOAD_LIMIT))
                dispatch(CreatePanelActions.setProjectsOfCustomerCount(data.projectsCount))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(hierarchyAssignmentsEditorActions.loadProjectsFailure(error))
            })
    }
}

export const loadMoreTasks = () => (dispatch, getState) => {
    const state = getState()
    if (needMoreTasks(state)) {
        const assignedIds = CreatePanelSelectors.getManagementAssignmentsCptIds(state)
        const projectId = getEditingLevel(state).projectId
        const onlyOpen = showOnlyActiveEntities(state)
        const searchText = getTasksSearchText(state)
        const offset = getTaskIdsOrder(state).length

        dispatch(createActionObject(ActionTypes.LOAD_TASK_ASSIGNMENTS_EDITOR_DATA))
        rpcCall('UserManagementService.getTaskManagementAssignmentsEditorDataForCreateUser', [assignedIds, projectId, onlyOpen, searchText, offset, LOAD_LIMIT])
            .then(data => {
                const tasks = getTasks(getState())
                const assignedTaskIdsInCurrentProject = assignedIds.taskIds.filter(id => tasks[id].projectId === projectId)
                data = {
                    ...data,
                    futureTasksAssigned: assignedIds.projectIdsExcludingSelf.includes(projectId),
                    someTasksAssigned: data.tasksCount - assignedTaskIdsInCurrentProject.length > 0 && assignedTaskIdsInCurrentProject.length !== 0,
                    allTasksAssigned: data.tasksCount === assignedTaskIdsInCurrentProject.length && assignedTaskIdsInCurrentProject.length !== 0,
                }
                dispatch(EntitiesActions.addTasks(data['tasks'].map(t => t['task'])))
                dispatch(hierarchyAssignmentsEditorActions.loadTasksSuccess(data, LOAD_LIMIT))
                dispatch(CreatePanelActions.setTasksOfProjectCount(data.tasksCount))
            })
            .catch(error => {
                dispatch(closeEditor())
                dispatch(hierarchyAssignmentsEditorActions.loadTasksFailure(error))
            })
    }
}

export const changeCustomerAssignment = (customerId, assigned) => (dispatch, getState) => {
    dispatch(CreatePanelActions.changeCustomerAssignments({
        addedIds: assigned ? [customerId] : [],
        removedIds: assigned ? [] : [customerId]
    }))

    const ma = CreatePanelSelectors.getManageAssignments(getState())

    dispatch(createActionObject(ActionTypes.CHANGE_CUSTOMER_ASSIGNMENT_SUCCESS, {
        customerId,
        canWork: false,
        canManage: assigned,
        someAssigned: ma.customersCount - ma.addedCustomerIds.length > 0 && ma.addedCustomerIds.length !== 0,
        allAssigned: ma.customersCount === ma.addedCustomerIds.length && ma.addedCustomerIds.length !== 0,
    }))

    if (!assigned) {
        dispatch(hierarchyAssignmentsEditorActions.clearCustomersTasksCount([customerId]))
        dispatch(hierarchyAssignmentsEditorActions.clearCustomersProjectsCount([customerId]))
    }


    dispatch(setManagementAssignmentsCountsLoaded(false))
    dispatch(AssignmentsPanelActions.clearTabsLoaded())
}

export const changeProjectAssignment = (projectId, assigned) => (dispatch, getState) => {
    dispatch(CreatePanelActions.changeProjectAssignments({
        addedIds: assigned ? [projectId] : [],
        removedIds: assigned ? [] : [projectId]
    }))

    const ma = CreatePanelSelectors.getManageAssignments(getState())

    dispatch(createActionObject(ActionTypes.CHANGE_PROJECT_ASSIGNMENT_SUCCESS, {
        projectId,
        canWork: false,
        canManage: assigned,
        someAssigned: ma.projectsOfCustomerCount - ma.addedProjectIds.length > 0 && ma.addedProjectIds.length !== 0,
        allAssigned: ma.projectsOfCustomerCount === ma.addedProjectIds.length && ma.addedProjectIds.length !== 0,
    }))

    if (!assigned) {
        dispatch(hierarchyAssignmentsEditorActions.clearProjectsTasksCount([projectId]))
    }

    dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
    dispatch(setManagementAssignmentsCountsLoaded(false))
    dispatch(AssignmentsPanelActions.clearTabsLoaded())
}

export const changeTaskAssignment = (taskId, assigned) => (dispatch, getState)  => {
    dispatch(CreatePanelActions.changeTasksAssignments({
        addedIds: assigned ? [taskId] : [],
        removedIds: assigned ? [] : [taskId]
    }))

    const ma = CreatePanelSelectors.getManageAssignments(getState())

    dispatch(createActionObject(ActionTypes.CHANGE_TASK_ASSIGNMENT_SUCCESS, {
        taskId,
        canWork: false,
        canManage: assigned,
        someAssigned: ma.tasksOfProjectCount - ma.addedTaskIds.length > 0 && ma.addedTaskIds.length !== 0,
        allAssigned: ma.tasksOfProjectCount === ma.addedTaskIds.length && ma.addedTaskIds.length !== 0,
    }))

    dispatch(createActionObject(ActionTypes.CLEAR_PROJECTS))
    dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
    dispatch(setManagementAssignmentsCountsLoaded(false))
    dispatch(AssignmentsPanelActions.clearTabsLoaded())
}

export const changeAllCustomersAssignments = (assign) => (dispatch, getState) => {
    const successHandler = customerIds => {
        const manageableCustomerIds = customerIds.reduce((map, cur) => {
            map[cur] = assign
            return map
        }, {})
        dispatch(createActionObject(ActionTypes.ASSIGN_ALL_CUSTOMERS_SUCCESS, {
            canManageCustomerIds: manageableCustomerIds,
            allCustomersAssigned: assign,
            someCustomersOrLowerAssigned: assign,
        }))
        dispatch(CreatePanelActions.changeCustomerAssignments({
            addedIds: assign ? customerIds : [],
            removedIds: assign ? [] : customerIds,
        }))

        dispatch(setManagementAssignmentsCountsLoaded(false))
        dispatch(AssignmentsPanelActions.clearTabsLoaded())
    }

    dispatch(createActionObject(ActionTypes.ASSIGN_ALL_CUSTOMERS))

    if (assign) {
        rpcCall('UserManagementService.getExistingCustomerIdsForManagementAssignmentsEditor', [])
            .then(customerIds => successHandler(customerIds))
            .catch(error => {
                dispatch(createActionObject(ActionTypes.ASSIGN_ALL_CUSTOMERS_FAILURE, {
                    error
                }))
            })
    } else {
        const customerIds = Object.keys(getCustomers(getState()))
        successHandler(customerIds)
        dispatch(hierarchyAssignmentsEditorActions.clearCustomersTasksCount(customerIds))
        dispatch(hierarchyAssignmentsEditorActions.clearCustomersProjectsCount(customerIds))
    }
}

export const changeAllProjectsAssignments = (assign) => (dispatch, getState) => {
    const successHandler = projectIds => {
        const canManageProjectIds = projectIds.reduce((map, cur) => {
            map[cur] = assign
            return map
        }, {})
        dispatch(createActionObject(ActionTypes.ASSIGN_ALL_PROJECTS_SUCCESS, {
            canManageProjectIds,
            allProjectsAssigned: assign,
            someProjectsOrLowerAssigned: assign,
        }))
        dispatch(CreatePanelActions.changeProjectAssignments({
            addedIds: assign ? projectIds : [],
            removedIds: assign ? [] : projectIds,
        }))

        dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
        dispatch(setManagementAssignmentsCountsLoaded(false))
        dispatch(AssignmentsPanelActions.clearTabsLoaded())
    }

    dispatch(createActionObject(ActionTypes.ASSIGN_ALL_PROJECTS))
    const customerId = getEditingLevel(getState()).customerId

    if (assign) {
        rpcCall('UserManagementService.getProjectIdsOfCustomerForManagementAssignmentsEditor', [customerId])
            .then(projectIds => successHandler(projectIds))
            .catch(error => {
                dispatch(createActionObject(ActionTypes.ASSIGN_ALL_PROJECTS_FAILURE, {error}))
            })
    } else {
        const projectIds = Object.values(getProjects(getState())).filter(project => project.customerId === customerId).map(p => p.id)
        successHandler(projectIds)
        dispatch(hierarchyAssignmentsEditorActions.clearProjectsTasksCount(projectIds))
    }
}

export const changeAllTasksAssignments = (assign) => (dispatch, getState) => {
    const successHandler = taskIds => {
        const canManageTaskIds = taskIds.reduce((map, cur) => {
            map[cur] = assign
            return map
        }, {})
        dispatch(createActionObject(ActionTypes.ASSIGN_ALL_TASKS_SUCCESS, {
            canManageTaskIds,
            allTasksAssigned: assign,
            someTasksAssigned: assign,
        }))
        dispatch(CreatePanelActions.changeTasksAssignments({
            addedIds: assign ? taskIds : [],
            removedIds: assign ? [] : taskIds
        }))

        dispatch(createActionObject(ActionTypes.CLEAR_PROJECTS))
        dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
        dispatch(setManagementAssignmentsCountsLoaded(false))
        dispatch(AssignmentsPanelActions.clearTabsLoaded())
    }

    dispatch(createActionObject(ActionTypes.ASSIGN_ALL_TASKS))
    const projectId = getEditingLevel(getState()).projectId

    if (assign) {
        rpcCall('UserManagementService.getTaskIdsOfProjectForManagementAssignmentsEditor', [projectId])
            .then(taskIds => successHandler(taskIds))
            .catch(error => {
                dispatch(createActionObject(ActionTypes.ASSIGN_ALL_TASKS_FAILURE, {
                    error
                }))
            })
    } else {
        const taskIds = Object.values(getTasks(getState()))
                              .filter(task => task.projectId === projectId)
                              .map(t => t.id)
        successHandler(taskIds)
    }
}

export const changeOnlyActive = onlyActive => dispatch => {
    dispatch(createActionObject(ActionTypes.ONLY_ACTIVE_ENTITIES_SET, onlyActive))
    dispatch(createActionObject(ActionTypes.CLEAR_TASKS))
    dispatch(createActionObject(ActionTypes.CLEAR_PROJECTS))
    dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
}

export const changeAllExistingAndFutureProjectsAssignment = (futureProjectsAssigned) => (dispatch, getState) => {
    const state = getState()
    const customerId = getEditingLevel(state).customerId

    const addedCustomerIds = futureProjectsAssigned ? [customerId] : []
    const removedCustomerIds = futureProjectsAssigned ? [] : [customerId]
    const projectIds = Object.values(getProjects(getState()))
                             .filter(project => project.customerId === customerId)
                             .map(p => p.id)

    dispatch(createActionObject(LOAD_PROJECT_ASSIGNMENTS_EDITOR_DATA_SUCCESS, {
        canManageParentCustomer: false,
        projectIds: [],
        canWorkProjectIds: [],
        canManageProjectIds: futureProjectsAssigned ? projectIds : [],
        canManageProjectExcludeSelfIds: [],
        viewerCanManageProjectIds: [],
        taskCounts: {},
        futureProjectsAssigned: futureProjectsAssigned,
        someProjectsOrLowerAssigned: true,
        allProjectsAssigned: true
    }))

    dispatch(CreatePanelActions.changeProjectAssignments({
        addedIds: futureProjectsAssigned ? [] : projectIds,
        addedCustomerIds,
        removedCustomerIds,
    }))

    dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
    dispatch(setManagementAssignmentsCountsLoaded(false))
    dispatch(AssignmentsPanelActions.clearTabsLoaded())
}

export const changeAllExistingAndFutureTasksAssignment = (futureTasksAssigned) => (dispatch, getState) => {

    const state = getState()
    const projectId = getEditingLevel(state).projectId

    const addedProjectIds = futureTasksAssigned ? [projectId] : []
    const removedProjectIds = futureTasksAssigned ? [] : [projectId]

    const taskIds = Object.values(getTasks(getState()))
                          .filter(task => task.projectId === projectId)
                          .map(t => t.id)

    dispatch(createActionObject(LOAD_TASK_ASSIGNMENTS_EDITOR_DATA_SUCCESS, {
        canManageParentCustomer: false,
        canManageParentProject: false,
        taskIds: [],
        canWorkTaskIds: [],
        canManageTaskIds: futureTasksAssigned ? taskIds : [],
        futureTasksAssigned: futureTasksAssigned,
        someTasksAssigned: true,
        allTasksAssigned: true
    }))

    dispatch(CreatePanelActions.changeTasksAssignments({
        addedIds: futureTasksAssigned ? [] : taskIds,
        addedProjectIds,
        removedProjectIds,
    }))

    dispatch(createActionObject(ActionTypes.CLEAR_PROJECTS))
    dispatch(createActionObject(ActionTypes.CLEAR_CUSTOMERS))
    dispatch(setManagementAssignmentsCountsLoaded(false))
    dispatch(AssignmentsPanelActions.clearTabsLoaded())
}

export const changeCustomersSearchText = hierarchyAssignmentsEditorActions.changeCustomersSearchText
export const clearCustomersSearchText = hierarchyAssignmentsEditorActions.clearCustomersSearchText

export const changeProjectsSearchText = hierarchyAssignmentsEditorActions.changeProjectsSearchText
export const clearProjectsSearchText = hierarchyAssignmentsEditorActions.clearProjectsSearchText

export const changeTasksSearchText = hierarchyAssignmentsEditorActions.changeTasksSearchText
export const clearTasksSearchText = hierarchyAssignmentsEditorActions.clearTasksSearchText



// WEBPACK FOOTER //
// ./web/js/administration/userManagement/createUserManagementAssignmentsEditor/actions.js